// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { EMPTY, Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

// Custom packages
import { CookieService } from "ngx-cookie-service";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class CompaniesService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private cookieService: CookieService
  ) {}

  /**
   * Create a new company
   *
   * @since 1.0.0
   *
   * @param company Company data
   */
  create(company: any): Observable<any> {
    return this.apiService.apiCall("POST", "companies", company);
  }

  /**
   * Add a subUser to company subUsers
   *
   * @since 1.0.0
   */
  createSubUser(companyId: string, subUser: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/sub-users`,
      subUser
    );
  }

  /**
   * Add a subProperty to company subProperties
   *
   * @since 1.0.0
   */
  createSubProperty(companyId: string, subProperty: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/sub-properties`,
      subProperty
    );
  }

  /**
   * Add a doc to company
   *
   * @since 1.0.0
   */
  createDoc(companyId: string, doc: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/documents`,
      doc
    );
  }

  /**
   * Add a new folder
   *
   * @since 1.0.0
   */
  createFolder(companyId: string, doc: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/new-folder`,
      doc
    );
  }

  /**
   * Add a contact to company phonebook
   *
   * @since 1.0.0
   */
  createPhonebookContact(
    companyId: string,
    phonebookContact: any
  ): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/phonebook`,
      phonebookContact
    );
  }

  /**
   * Import contacts to company phonebook
   *
   * @since 1.0.0
   */
  importPhonebookContact(
    companyId: string,
    phonebookContact: any
  ): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `companies/${companyId}/phonebook/import`,
      phonebookContact
    );
  }

  /**
   * Get given companyId data from back-end
   *
   * @since 1.0.0
   *
   * @param companyId ID of the company to get
   */
  get(companyId: string): Observable<any> {
    return this.apiService.apiCall("GET", `companies/${companyId}`).pipe(
      catchError((err) => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given companyId data from back-end
   *
   * @since 1.0.0
   *
   * @param companyId ID of the company to get
   */
  getBaseData(companyId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `companies/${companyId}/base-data`)
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get given companyId BiPy subscription status
   *
   * @since 1.0.0
   *
   * @param companyId ID of the company to get
   */
  getBipyStatus(companyId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `companies/${companyId}/bipy-status`)
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get all companies with subscription suppliers
   *
   * @since 1.0.0
   *
   */
  getSuppliersActive(): Observable<any> {
    return this.apiService.apiCall("GET", `companies/suppliers-active`).pipe(
      catchError((err) => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get companies list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    if (!sort) {
      sort = "name";
      sortVersus = "asc";
    }
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "companies", params);
  }

  /**
   * Get companies list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getListAll(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    if (!sort) {
      sort = "name";
      sortVersus = "asc";
    }
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "companies/list-all", params);
  }

  /**
   * Get companies list for given query params (only admin companies)
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getListAdmin(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      search = {
        ...search,
        types: ["manager"],
      };
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "companies", params);
  }

  /**
   * Get companies list for given query params (only admin companies)
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getListOwner(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      search = {
        ...search,
        types: ["owner"],
      };
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "companies", params);
  }

  /**
   * Get an exportation file of all compananies
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall("GET", "companies/export", {}, { responseType: "blob" })
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all compananies
   *
   * @since 1.0.0
   */
  getExportSuspended(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "companies/export-suspended",
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all compananies
   *
   * @since 1.0.0
   */
  getExportPhonebook(companyId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `companies/${companyId}/export-phonebook`,
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all compananies
   *
   * @since 1.0.0
   */
  getExportPhonebookAdmin(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `companies/export-phonebook-admin`,
        {},
        { responseType: "blob" }
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get subUsers list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubUsersList(
    companyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    return this.apiService.apiCall(
      "GET",
      `companies/${companyId}/sub-users`,
      params
    );
  }

  /**
   * Get subProperties list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSubPropertiesList(
    companyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any,
    userId?: string
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus, userId };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      `companies/${companyId}/sub-properties`,
      params
    );
  }

  /**
   * Get phonebook list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results numbers
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPhonebookList(
    companyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      `companies/${companyId}/phonebook`,
      params
    );
  }

  /**
   * Get phonebook list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results numbers
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPhonebookListMaster(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const companyId = this.cookieService.get("requestedCompany");
    if (!companyId) {
      return EMPTY;
    }
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      `companies/${companyId}/phonebook`,
      params
    );
  }

  /**
   * Get phonebook list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results numbers
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPhonebookListAdmin(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", `companies/phonebook-admin`, params);
  }

  /**
   * Get company total docs
   *
   * @since 1.0.0
   *
   * @param
   */
  getCompanyTotalDocs(companyId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/company-total-docs`, { companyId })
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  getPropertiesWithSupplierAgreement(companyId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `companies/${companyId}/properties-with-supplier-agreement`,
        {}
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get suspended companies list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSuspendedList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "companies/suspended", params);
  }

  /**
   * Get an exportation technical file of company
   *
   * @since 1.0.0
   */
  getTechnicalFile(companyId: string): Observable<any> {
    return this.apiService.apiCall(
      'GET', 
      `companies/${companyId}/export-technical-file`, 
      {}, 
      undefined,
      undefined,
      undefined,
      'v2'
    ).pipe(
      catchError(err => {
        return this.helperService.parseErrorBlob(err);
      })
    );
  }

  /**
   * Update company with given companyId with given company data
   *
   * @since 1.0.0
   *
   * @param companyId ID of company to update
   * @param data New company's data
   */
  updateDisclaimer(companyId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `companies/${companyId}`, data);
  }

  /**
   * Update company with given companyId with given company data
   *
   * @since 1.0.0
   *
   * @param companyId ID of company to update
   * @param data New company's data
   */
  updateFields(companyId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `companies/${companyId}/fields`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Determine whether the masterCode column should be visible for the
   * given role/company combination. Only master/collaborator roles are
   * gated by the company's showMasterCode setting; every other role
   * always sees the column (unchanged behaviour).
   *
   * @since 3.0.0
   */
  shouldShowMasterCodeColumn$(roleType: string, companyId: string): Observable<boolean> {
    if (roleType !== "master" && roleType !== "collaborator") {
      return of(true);
    }
    if (!companyId) {
      return of(false);
    }
    return this.get(companyId).pipe(
      map((res) => !!(res && res.status && res.data && res.data.showMasterCode))
    );
  }

  /**
   * This update supplier number associated to company master
   *
   * @since 1.0.0
   *
   * @param companyId ID of company to update
   */
  updateSuppliersNumber(companyId: string): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `companies/${companyId}/update-suppliers-number`,
      {}
    );
  }

  /**
   * Update company with given companyId with given company data
   *
   * @since 1.0.0
   *
   * @param companyId ID of company to update
   * @param data New company's data
   */
  update(companyId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `companies/${companyId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update given subUserId
   *
   * @since 1.0.0
   */
  updateSubUser(
    companyId: string,
    subUserId: string,
    data: Object
  ): Observable<any> {
    return this.apiService
      .apiCall("PUT", `companies/${companyId}/sub-users/${subUserId}`, data)
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Toggle given subProperty's activation
   *
   * @since 1.0.0
   */
  toggleSubProperty(
    companyId: string,
    subPropertyId: string,
    data: any
  ): Observable<any> {
    return this.apiService
      .apiCall(
        "PUT",
        `companies/${companyId}/sub-properties/${subPropertyId}`,
        data,
        { contentType: "auto" }
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update given phonebookContactId
   *
   * @since 1.0.0
   */
  updatePhonebookContact(
    companyId: string,
    phonebookContactId: string,
    data: Object
  ): Observable<any> {
    return this.apiService
      .apiCall(
        "PUT",
        `companies/${companyId}/phonebook/${phonebookContactId}`,
        data
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Activate company
   *
   * @since 1.0.0
   *
   * @param companyId ID of the company
   */
  activate(companyId: string): Observable<any> {
    return this.apiService.apiCall("POST", `companies/${companyId}/activate`);
  }

  /**
   * Delete permanently a company
   *
   * @since 1.0.0
   *
   * @param companyId ID of the company
   */
  delete(companyId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `companies/${companyId}`);
  }

  /**
   * Delete permanently a company's subUser
   *
   * @since 1.0.0
   */
  deleteSubUser(companyId: string, subUserId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `companies/${companyId}/sub-users/${subUserId}`
    );
  }

  /**
   * Delete permanently a company's subProperty
   *
   * @since 1.0.0
   */
  deleteSubProperty(companyId: string, subPropertyId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `companies/${companyId}/sub-properties/${subPropertyId}`
    );
  }

  /**
   * Delete permanently a company's phonebook
   *
   * @since 1.0.0
   */
  deletePhonebookContact(
    companyId: string,
    phonebookContactId: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `companies/${companyId}/phonebook/${phonebookContactId}`
    );
  }

  /**
   * Delete permanently a company's document
   *
   * @since 1.0.0
   */
  deleteDoc(companyId: string, docId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `companies/${companyId}/documents/${docId}`
    );
  }

  /**
   * Delete permanently a company's document
   *
   * @since 1.0.0
   */
  deleteFolder(companyId: string, pathToDelete: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `companies/${companyId}/delete-folder`,
      { pathToDelete }
    );
  }
}
